import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Chat } from '../types';
import { getUserChats } from '../services/api/chatService';
import { useAuth } from './useAuth';

interface ChatManagerContextType {
  chats: Chat[];
  currentChatId: string | null;
  isLoading: boolean;
  error: string | null;
  setCurrentChatId: (chatId: string | null) => void;
  refreshChats: () => Promise<void>;
  addNewChat: (chat: Chat) => void;
  updateChatTitle: (chatId: string, title: string) => void;
  clearCurrentChat: () => void;
}

const ChatManagerContext = createContext<ChatManagerContextType | undefined>(undefined);

interface ChatManagerProviderProps {
  children: ReactNode;
}

export const ChatManagerProvider: React.FC<ChatManagerProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [chats, setChats] = useState<Chat[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchChats = useCallback(async () => {
    // Use id field (API expects id, not userid)
    const userId = user?.id;

    if (!userId) {
      console.log('⚠️ No user ID available, skipping chat fetch. User object:', user);
      return;
    }

    console.log('🔄 Fetching chats for user ID:', userId);
    setIsLoading(true);
    setError(null);

    try {
      const response = await getUserChats(String(userId));
      console.log('✅ Fetched chats:', response.total_chats);

      // Convert UserChat[] to Chat[] format
      const convertedChats: Chat[] = response.chats.map(chat => ({
        id: chat.chat_id,
        title: chat.title,
        lastMessage: undefined,
        createdAt: chat.created_at,
        updatedAt: chat.last_activity,
        userId: String(chat.user_id)
      }));

      // Sort by last_activity descending (newest first)
      const sortedChats = convertedChats.sort((a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );

      setChats(sortedChats);
    } catch (err) {
      console.error('❌ Failed to fetch chats:', err);
      setError('Failed to load chats');
      // Don't throw error, just log it
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, user]);

  // Fetch user's chats on mount and when user changes
  useEffect(() => {
    console.log('🔍 useChatManager useEffect triggered. User ID:', user?.id);

    if (user?.id) {
      fetchChats();
    }
  }, [user?.id, fetchChats]);

  const refreshChats = useCallback(async () => {
    await fetchChats();
  }, [fetchChats]);

  const addNewChat = useCallback((chat: Chat) => {
    setChats(prev => [chat, ...prev]);
  }, []);

  const updateChatTitle = useCallback((chatId: string, title: string) => {
    setChats(prev => prev.map(chat =>
      chat.id === chatId ? { ...chat, title } : chat
    ));
  }, []);

  const clearCurrentChat = useCallback(() => {
    setCurrentChatId(null);
  }, []);

  return (
    <ChatManagerContext.Provider
      value={{
        chats,
        currentChatId,
        isLoading,
        error,
        setCurrentChatId,
        refreshChats,
        addNewChat,
        updateChatTitle,
        clearCurrentChat,
      }}
    >
      {children}
    </ChatManagerContext.Provider>
  );
};

export const useChatManager = (): ChatManagerContextType => {
  const context = useContext(ChatManagerContext);
  if (context === undefined) {
    throw new Error('useChatManager must be used within a ChatManagerProvider');
  }
  return context;
};
